import { inject, Injectable } from "@angular/core";
import { Router } from "@angular/router";
import { AngularPlugin } from "@microsoft/applicationinsights-angularplugin-js";
import { ApplicationInsights, DistributedTracingModes } from "@microsoft/applicationinsights-web";
import { BROWSER_SESSION_ID } from "@pcp/logging";
import { PCP_INSIGHTS_CONFIG } from "../injection-tokens";
import { PcpInsightsErrorHandler } from "./pcp-insights-error-handler";
import { IPcpApplicationInsights } from "../types/application-insights-interface";
import { shouldDropTelemetry } from "./telemetry-filters";
import { echoTelemetryToConsole } from "./telemetry-echo";


@Injectable({
    providedIn: 'root'
})
export class ApplicationInsightsFactoryService {

    private readonly router = inject(Router);
    private readonly config = inject(PCP_INSIGHTS_CONFIG);
    private readonly browserSessionId = inject(BROWSER_SESSION_ID);

    create(): IPcpApplicationInsights {
        const angularPlugin = new AngularPlugin();
        const appInsights = new ApplicationInsights({
            config: {
                enableAjaxErrorStatusText: true,
                distributedTracingMode: DistributedTracingModes.AI_AND_W3C,
                disableCorrelationHeaders: false,
                enableAjaxPerfTracking: true,
                connectionString: this.config.connectionString,

                // SdkStats (SDK >= 3.4.3) is left ON deliberately — do not "clean this up".
                // It is on by default and cannot be turned off via the `sdkStats` key (that
                // only carries the flush interval); disabling needs
                // `featureOptIn: { SdkStats: { mode: FeatureOptInMode.disable } }`.
                // Decided 2026-09-30: it reports SDK pipeline health as `customMetrics`
                // (`Item_Success_Count` / `Item_Dropped_Count` / `Item_Retry_Count`, one per
                // telemetry type per flush, measured ~6 items/hour/client ≈ +7% of client
                // telemetry — well inside the ingestion cap). It earns that: the verification
                // session surfaced `dropCode: CLIENT_EXCEPTION` drops that are otherwise
                // invisible. NOTE the items are metrics, so they are in `customMetrics`, never
                // `customEvents` — that mismatch already cost one failed verification.

                // enableUnhandledPromiseRejectionTracking: true,
                // enableRequestHeaderTracking: true,
                // enableResponseHeaderTracking: true,
                // enableCorsCorrelation: true,

                // following configs are disabled: they create a lot of additional entries without meaningful value
                // autoTrackPageVisitTime: true,
                // enableAutoRouteTracking: true,

                extensions: [
                    <any>angularPlugin
                ],
                extensionConfig: {
                    [angularPlugin.identifier]: {
                        router: this.router,
                        errorServices: [new PcpInsightsErrorHandler()],
                    },

                },
                correlationHeaderExcludedDomains: [
                    // Disable processing of correlation headers from vimeo.
                    // Not disabling would result in errors like:
                    // ```
                    // Access to XMLHttpRequest at 'https://vimeo.com...' from origin 'http://...' has been blocked by CORS policy:
                    // Request header field traceparent is not allowed by Access-Control-Allow-Headers in preflight response.
                    // ```
                    'vimeo.com',
                ]
            }
        });
        appInsights.loadAppInsights();

        // Drop expected noise (image-polling 404s, SignalR negotiate 401s) before sending.
        // Returning false from a telemetry initializer prevents the item from being sent.
        appInsights.addTelemetryInitializer(envelope =>
            shouldDropTelemetry(envelope) ? false : undefined
        );

        appInsights.addTelemetryInitializer(envelope => {
            if (!envelope.tags) {
                envelope.tags = [];
            }
            envelope.tags["ai.cloud.role"] = this.config.cloudRoleName;

            // Stamp browser_session_id on every telemetry item for per-tab correlation in Kusto
            envelope.data = { ...envelope.data, browser_session_id: this.browserSessionId };
        });

        // Off unless `pcp-log-level` is overridden to DEBUG or below. Registered last, and prints
        // one microtask later still, so it shows the item after every initializer — including the
        // lesson-meta ones the apps register at init. Returns nothing, so it drops no item.
        appInsights.addTelemetryInitializer(echoTelemetryToConsole);

        appInsights.context.application.ver = this.config.applicationVersion;
        // insights.context.application.build = '789.789.789';

        return appInsights;
    }
}
